export type RGBAColor = [number, number, number, number];
export type OKLabColor = [number, number, number, number];

export function parseColorToRgba(color: string): RGBAColor | null {
  const value = color.trim().toLowerCase();

  if (value === "transparent") {
    return [0, 0, 0, 0];
  }

  const rgbMatch = value.match(/^rgba?\((.+)\)$/);
  if (rgbMatch) {
    const parts = rgbMatch[1].split(",").map(part => part.trim());
    if (parts.length < 3 || parts.length > 4) return null;

    const r = Number(parts[0]);
    const g = Number(parts[1]);
    const b = Number(parts[2]);
    const a = parts.length === 4 ? Number(parts[3]) : 1;

    if ([r, g, b, a].some(Number.isNaN)) return null;

    return [
      clamp(r, 0, 255),
      clamp(g, 0, 255),
      clamp(b, 0, 255),
      clamp(a, 0, 1)
    ];
  }

  const hexMatch = value.match(/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (hexMatch) {
    const hex = hexMatch[1];
    if (hex.length === 3 || hex.length === 4) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      const a = hex.length === 4 ? parseInt(hex[3] + hex[3], 16) / 255 : 1;
      return [r, g, b, clamp(a, 0, 1)];
    }

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
    return [r, g, b, clamp(a, 0, 1)];
  }

  return null;
}

export function rgbaToOklab(rgba: RGBAColor): OKLabColor {
  const [r, g, b, alpha] = rgba;

  const lr = srgbToLinear(clamp(r, 0, 255) / 255);
  const lg = srgbToLinear(clamp(g, 0, 255) / 255);
  const lb = srgbToLinear(clamp(b, 0, 255) / 255);

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  const okl = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
  const oka = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
  const okb = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;

  return [okl, oka, okb, clamp(alpha, 0, 1)];
}

export function oklabToRgba(oklab: OKLabColor): RGBAColor {
  const [okl, oka, okb, alpha] = oklab;

  const l_ = okl + 0.3963377774 * oka + 0.2158037573 * okb;
  const m_ = okl - 0.1055613458 * oka - 0.0638541728 * okb;
  const s_ = okl - 0.0894841775 * oka - 1.2914855480 * okb;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  const lr = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const lg = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const lb = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;

  const sr = linearToSrgb(lr);
  const sg = linearToSrgb(lg);
  const sb = linearToSrgb(lb);

  return [
    clamp(Math.round(sr * 255), 0, 255),
    clamp(Math.round(sg * 255), 0, 255),
    clamp(Math.round(sb * 255), 0, 255),
    clamp(alpha, 0, 1)
  ];
}

function srgbToLinear(x: number): number {
  if (x <= 0.04045) return x / 12.92;
  return Math.pow((x + 0.055) / 1.055, 2.4);
}

function linearToSrgb(x: number): number {
  const v = clamp(x, 0, 1);
  if (v <= 0.0031308) return 12.92 * v;
  return 1.055 * Math.pow(v, 1 / 2.4) - 0.055;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
